import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import type { Future } from '@warp-drive/core/request';
import type { TypedRecordInstance } from '@warp-drive/core/types/record';
import type { WithLegacy } from '@warp-drive/legacy/model/migration-support';
import { modifier } from 'ember-modifier';

import { deepHasChanged } from '../data/deep-has-changed.ts';
import { deepRollback } from '../data/deep-rollback.ts';
import { deepSave, DeepSaveError } from '../data/deep-save.ts';
import { staysEditableAfterError } from '../data/stays-editable-after-error.ts';
import { type EditingContext, EditingStoppedReason } from './edit-context.gts';
import { Validation } from './validation.gts';

export interface EditRecordSignature<T, A extends object = object> {
  Args: {
    /**
     * The item to edit.
     */
    item: T;
    /**
     * The editing status.
     */
    context: EditingContext;
  } & A;
  Element: HTMLFormElement;
}

export interface MySignatureBase<
  T,
  A extends object = object,
  B extends object = object,
> extends EditRecordSignature<T> {
  Args: {
    /**
     * The item to edit.
     */
    item: T;
    /**
     * The editing status.
     */
    context: EditingContext;
    /**
     * The label to display for the edit button.
     */
    editLabel: string;
    /**
     * The callback to call when the edit button is clicked. Optional.
     */
    onEdit?: () => void;
    /**
     * The callback to call when the form needs to be validated before saving.
     */
    validate?: (item: T) => Validation;
    /**
     * The callback to call when the save button is clicked.
     * If not provided, the changes will be saved using the `deepSave` utility.
     */
    save?: () => Future<unknown> | Promise<unknown>;
    /**
     * The callback to call when the revert button is clicked.
     * If not provided, the changes will be reverted using the `deepRollback` utility.
     */
    revert?: () => void;
    /**
     * The size of the buttons.
     */
    buttonSize?: 'btn-sm' | 'btn-md' | 'btn-lg';
    /**
     * The initial state of the form. Defaults to 'readonly'.
     */
    initialState?: 'readonly' | 'edit';
    /**
     * Whether the user may edit. Defaults to true. When false the form is
     * locked read-only and the Edit control is hidden — use it to gate edit
     * access behind an ability while still letting viewers see the record.
     */
    canEdit?: boolean;
    /**
     * The maximum depth to traverse relationships when saving.
     */
    maxDepth: number;
    /**
     * When true, validation errors are not shown in the default alert banner.
     */
    hideValidationMessage?: boolean;
    /**
     * The label to display for the save button. Defaults to 'Save'.
     */
    saveLabel?: string;
    /**
     * The label to display for the cancel button. Defaults to 'Cancel'.
     */
    cancelLabel?: string;
    /**
     * Optional extra unsaved-change check (e.g. staged profile photo draft).
     */
    hasUnsavedChanges?: () => boolean;
  } & A;
  Blocks: {
    /**
     * The block to render the form.
     */
    form: [
      uniqueId: string,
      disabled: boolean,
      readonly?: boolean,
      validation?: Validation,
      cancel?: (event: Event) => void,
    ];
    /**
     * The block to render the buttons. Optional.
     */
    buttons?: [uniqueId: string, readonly: boolean, validation?: Validation];

    /*
     * The block to render the help text. Optional.
     */
    help?: [];
  } & B;
}

abstract class EditRecordPrivate<
  T extends WithLegacy<TypedRecordInstance>,
  A extends object = object,
  B extends object = object,
> extends Component<MySignatureBase<T, A, B>> {
  @tracked private _readonly =
    this.args.initialState !== 'edit' || this.args.canEdit === false;
  protected _formElement: HTMLFormElement | null = null;

  get readonly() {
    return this._readonly;
  }

  get saveLabel() {
    return this.args.saveLabel || 'Save';
  }

  get cancelLabel() {
    return this.args.cancelLabel || 'Cancel';
  }

  /**
   * Whether the user may edit this record. Defaults to true.
   */
  get canEdit() {
    return this.args.canEdit !== false;
  }

  /**
   * Starts the editing process.
   */
  startEditing() {
    if (!this.canEdit) {
      return;
    }

    if (this.args.context.isEditing) {
      console.log('Editing already started');
      return;
    }

    this._readonly = false;
    this.args.context.editingStarted();
  }

  /**
   * Stops the editing process.
   */
  stopEditing(reason: EditingStoppedReason) {
    this._formElement?.classList.remove('was-validated');

    const stayEditable =
      this.args.initialState === 'edit' &&
      reason === EditingStoppedReason.Saved;

    if (!stayEditable) {
      this._readonly = true;
    }

    this.args.context.editingStopped(reason);
  }
}

export abstract class EditRecordBase<
  T extends WithLegacy<TypedRecordInstance>,
  A extends object = object,
  B extends object = object,
> extends EditRecordPrivate<T, A, B> {
  @tracked saving = false;
  @tracked validation: Validation = Validation.success();
  @service declare store: Store;
  @service declare router: RouterService;

  /**
   * The function to call when the form is submitted.
   * Saves the changes to the record then sets the form to readonly.
   */
  submitClicked = (event: Event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    this._formElement = form;
    form.classList.add('was-validated');

    if (!form.checkValidity()) {
      const firstInvalid = form.querySelector<HTMLElement>(
        ':invalid:not(fieldset):not(form)'
      );
      firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      firstInvalid?.focus({ preventScroll: true });
      return;
    }

    this.validation =
      this.args.validate?.(this.args.item) ?? Validation.success();
    if (this.validation.isFailure) {
      if (this.args.hideValidationMessage) {
        this.validation = Validation.success();
      }
      return;
    }

    this.saving = true;

    void this.save()
      .then(() => {
        this.stopEditing(EditingStoppedReason.Saved);
        this.validation = Validation.success();
      })
      .catch((error: unknown) => {
        console.error('Save failed: ', error);
        if (
          !(error instanceof DeepSaveError) &&
          !staysEditableAfterError(error)
        ) {
          this.stopEditing(EditingStoppedReason.SaveFailed);
        }
        if (!this.args.hideValidationMessage) {
          this.validation = Validation.fromError(error as Error);
        }
      })
      .finally(() => {
        this.saving = false;
      });
  };

  /**
   * The function to call when the cancel button is clicked.
   * Reverts the changes and sets the form to readonly.
   */
  cancelClicked = (event: Event) => {
    event.preventDefault();
    this.revert();
    this.stopEditing(EditingStoppedReason.Cancelled);
    this.validation = Validation.success();
  };

  /**
   * The function to call when the edit button is clicked.
   * Sets the form to editable.
   */
  editClicked = (event: Event) => {
    event.preventDefault();
    this.startEditing();
    this.args.onEdit?.();
  };

  /**
   * Calls the `save` callback if provided, otherwise uses the `deepSave` utility.
   */
  save(): Future<unknown> | Promise<unknown> {
    if (this.args.save) {
      return this.args.save();
    } else {
      return deepSave(this.args.item, this.store, {
        maxDepth: this.args.maxDepth,
      });
    }
  }

  /**
   * Calls the `revert` callback if provided, otherwise uses the `deepRollback` utility.
   */
  revert(): void {
    if (this.args.revert) {
      this.args.revert();
    } else {
      deepRollback(this.args.item, this.store);
    }
  }

  /**
   * Returns true if the form is disabled or saving.
   */
  get isDisabled() {
    return this.saving || this.readonly;
  }

  get buttonSize() {
    return this.args.buttonSize || 'btn-sm';
  }

  /**
   * Intercepts the transition and prompts the user to confirm if there are unsaved changes.
   */
  interceptTransition = modifier(() => {
    const routeWillChange = (transition: Transition) => {
      if (transition.isAborted) {
        console.log('transition is aborted');
        return;
      }

      console.log('routeWillChange', transition);
      if (
        deepHasChanged(this.args.item, this.store) ||
        this.args.hasUnsavedChanges?.()
      ) {
        const confirmed = confirm(
          'You have unsaved changes. Are you sure you want to leave this page?'
        );
        if (confirmed) {
          console.log('reverting changes and continuing transition');
          this.revert();
        } else {
          console.log('aborting transition');
          transition.abort();
        }
      }
    };

    this.router.on('routeWillChange', routeWillChange);
    return () => {
      this.router.off('routeWillChange', routeWillChange);
    };
  });
}
